import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, API_URL } from '../api';
import usePolling from '../utils/usePolling';

// Hälsokontrollerna på servern körs var 30:e minut, så 5 min räcker (A11).
const INTERVALL_MS = 5 * 60 * 1000;

/**
 * Global alert-bar under header som varnar om någon tenant-integration
 * (Hogia, Fortnox, IA, ACC, Dalux ...) inte fungerar.
 *
 * Källa: GET /integrations/health/banner (returnerar bara provider+status,
 * inga error-strings — säkert för alla tenant-användare att se).
 *
 * Pollar var 5:e minut i synlig flik, pausar i dold flik (A11, 2026-10-07).
 * Visar inget vid status='ok'. Dismissible per session:
 * när användaren stänger sparas integration_id + status i localStorage;
 * ny status (status-byte eller ny integration nere) visar bannern igen.
 */
export default function IntegrationHealthBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem('kanplan-integration-banner-dismissed');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const navigate = useNavigate();

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_URL}/integrations/health/banner`);
      if (!res.ok) return false;
      const json = await res.json();
      setData(json);
      return true;
    } catch {
      // Tyst — bannern är icke-kritiskt. Vi vill inte spam:a felmodaler om
      // health-endpointen själv är tillfälligt nere. usePolling glesar ut.
      return false;
    }
  }, []);

  usePolling(fetchStatus, { intervalMs: INTERVALL_MS });

  if (!data || !data.failing || data.failing.length === 0) return null;

  // Filtrera bort de som användaren explicit dismiss:at (samma status + id).
  const dismissKey = (item) => `${item.integration_id}:${item.status}`;
  const visible = data.failing.filter(i => !dismissed[dismissKey(i)]);
  if (visible.length === 0) return null;

  const hasCritical = visible.some(i => i.status === 'critical');
  // Tokens ur index.css så att bannern följer mörkt läge (A10, 2026-10-01).
  const color = hasCritical ? 'var(--kp-red)' : 'var(--kp-amber)';
  // Knappens fyllnad: -fill-tokens, så att vit text klarar kontrastkravet även i mörkt läge.
  const fill = hasCritical ? 'var(--kp-red-fill)' : 'var(--kp-amber-fill)';
  const bg = hasCritical ? 'var(--kp-red-light)' : 'var(--kp-amber-light)';
  const borderColor = hasCritical ? 'rgba(220,38,38,0.25)' : 'rgba(217,119,6,0.25)';

  const PROVIDER_LABELS = {
    hogia: 'Hogia',
    hogia_sandbox: 'Hogia (sandbox)',
    fortnox: 'Fortnox',
    ia: 'IA-systemet',
    acc: 'Autodesk Construction Cloud',
    dalux: 'Dalux',
  };

  const list = visible
    .map(i => PROVIDER_LABELS[i.provider] || i.provider)
    .join(', ');

  const dismissAll = () => {
    const next = { ...dismissed };
    visible.forEach(i => { next[dismissKey(i)] = Date.now(); });
    setDismissed(next);
    try {
      localStorage.setItem('kanplan-integration-banner-dismissed', JSON.stringify(next));
    } catch {}
  };

  const goToIntegrations = () => {
    navigate('/tenant-admin?tab=integrations');
  };

  return (
    <div
      role="alert"
      style={{
        background: bg,
        borderBottom: `1px solid ${borderColor}`,
        padding: '10px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        /* flexWrap: på mobil kläms texten annars i en smal spalt bredvid
           knapparna — nu radbryter knappraden under texten vid behov */
        flexWrap: 'wrap',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      {/* flex-basis 240px: under den bredden radbryter knappgruppen ner
          under texten i stället för att klämma den till en smal spalt */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 240px', minWidth: 0 }}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ width: 18, height: 18, flexShrink: 0 }}
          aria-hidden
        >
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
        <div style={{ fontSize: 13.5, color: 'var(--kp-text)', lineHeight: 1.4, minWidth: 0 }}>
          <strong style={{ fontWeight: 700, color }}>
            {hasCritical ? 'Integration ur funktion:' : 'Integrationsvarning:'}
          </strong>{' '}
          {visible.length === 1
            ? `Kopplingen till ${list} fungerar inte just nu. Vissa funktioner kan vara påverkade.`
            : `Kopplingen till följande integrationer fungerar inte: ${list}. Vissa funktioner kan vara påverkade.`}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <button
          type="button"
          onClick={goToIntegrations}
          style={{
            padding: '5px 12px',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--kp-on-fill)',
            background: fill,
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            minHeight: 'auto',
            fontFamily: 'inherit',
          }}
        >
          Hantera integrationer
        </button>
        <button
          type="button"
          onClick={dismissAll}
          aria-label="Dölj varning"
          title="Dölj — varningen visas igen om statusen ändras eller nästa session"
          style={{
            width: 28,
            height: 28,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: '1px solid var(--kp-border)',
            borderRadius: 6,
            cursor: 'pointer',
            color: 'var(--kp-text-secondary)',
            minHeight: 'auto',
          }}
        >
          <svg viewBox="0 0 12 12" width={12} height={12} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 3l6 6M9 3l-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
